import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal, Map as MapIcon, List, BellPlus } from "lucide-react";
import { homesSearch, homesSaveSearch } from "@/lib/api";
import { PropertyCard } from "@/components/homes/PropertyCard";
import { ListingMap } from "@/components/homes/ListingMap";
import { PROPERTY_TYPES } from "@/components/homes/homesUtil";
import { useSaved } from "@/context/SavedContext";
import { login } from "@/context/AuthContext";

export default function HomesSearch() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("split"); // split | list | map (mobile)

  const params = useMemo(() => {
    const o = {};
    for (const [k, v] of sp.entries()) if (v) o[k] = v;
    o.page_size = 24;
    return o;
  }, [sp]);

  useEffect(() => {
    setLoading(true);
    homesSearch(params).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [params]);

  const set = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("page");
    setSp(next);
  };

  const status = sp.get("status") || "";
  const listings = data?.listings || [];
  const points = listings.map((l) => ({ id: l.id, lat: l.latitude, lng: l.longitude, price: l.price, rent: l.status === "for_rent", address: l.address }));
  const savedCtx = useSaved();

  const saveSearch = async () => {
    if (!savedCtx?.user) {
      toast("Sign in to save this search", { description: "Get alerted when new matching homes are listed." });
      login(window.location.pathname + window.location.search);
      return;
    }
    const p = {};
    for (const [k, v] of sp.entries()) if (v && k !== "page_size") p[k] = v;
    try { await homesSaveSearch(p); savedCtx.reload(); toast.success("Search saved — we'll track new matches"); }
    catch { toast.error("Could not save search"); }
  };

  const inp = "rounded-lg border border-[#0F172A]/15 bg-white px-3 py-2 text-sm text-[#334155] outline-none focus:border-[#0F172A]";

  return (
    <div data-testid="homes-search">
      {/* Filter bar */}
      <div className="sticky top-0 z-20 border-b border-[#0F172A]/10 bg-[#F8FAFC]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 px-4 py-3">
          <SlidersHorizontal className="h-4 w-4 text-[#0F172A]/50" />
          <div className="flex overflow-hidden rounded-lg border border-[#0F172A]/15">
            {[["", "All"], ["for_sale", "Buy"], ["for_rent", "Rent"]].map(([v, label]) => (
              <button key={label} onClick={() => set("status", v)}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${status === v ? "bg-[#0F172A] text-white" : "bg-white text-[#0F172A] hover:bg-[#0F172A]/5"}`}
                data-testid={`filter-status-${v || "all"}`}>{label}</button>
            ))}
          </div>
          <select value={sp.get("property_type") || ""} onChange={(e) => set("property_type", e.target.value)} className={inp} data-testid="filter-type">
            {PROPERTY_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
          <input value={sp.get("min_price") || ""} onChange={(e) => set("min_price", e.target.value)} placeholder="Min $" className={`${inp} w-24`} type="number" data-testid="filter-min-price" />
          <input value={sp.get("max_price") || ""} onChange={(e) => set("max_price", e.target.value)} placeholder="Max $" className={`${inp} w-24`} type="number" data-testid="filter-max-price" />
          <select value={sp.get("beds") || ""} onChange={(e) => set("beds", e.target.value)} className={inp} data-testid="filter-beds">
            <option value="">Beds</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={String(n)}>{`${n}+ bd`}</option>)}
          </select>
          <select value={sp.get("baths") || ""} onChange={(e) => set("baths", e.target.value)} className={inp} data-testid="filter-baths">
            <option value="">Baths</option>{[1, 2, 3].map((n) => <option key={n} value={String(n)}>{`${n}+ ba`}</option>)}
          </select>
          <select value={sp.get("sort") || "newest"} onChange={(e) => set("sort", e.target.value)} className={`${inp} ml-auto`} data-testid="filter-sort">
            <option value="newest">Newest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
          </select>
          <div className="flex overflow-hidden rounded-lg border border-[#0F172A]/15 lg:hidden">
            <button onClick={() => setView("list")} className={`px-3 py-2 ${view !== "map" ? "bg-[#0F172A] text-white" : "bg-white text-[#0F172A]"}`}><List className="h-4 w-4" /></button>
            <button onClick={() => setView("map")} className={`px-3 py-2 ${view === "map" ? "bg-[#0F172A] text-white" : "bg-white text-[#0F172A]"}`}><MapIcon className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-[#0F172A]" data-testid="homes-search-count">
            {loading ? "Searching…" : `${data?.total ?? 0} ${status === "for_rent" ? "rentals" : status === "for_sale" ? "homes for sale" : "listings"} found`}
          </div>
          <button onClick={saveSearch} className="inline-flex items-center gap-1.5 rounded-full border border-[#2563EB]/30 bg-[#EEF2FF] px-4 py-2 text-sm font-semibold text-[#2563EB] transition-colors hover:bg-[#2563EB] hover:text-white" data-testid="save-search-btn">
            <BellPlus className="h-4 w-4" /> Save search & alert me
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_460px]">
          {/* Results */}
          <div className={view === "map" ? "hidden lg:block" : ""}>
            {loading ? (
              <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></div>
            ) : listings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#0F172A]/20 p-12 text-center text-[#334155]/60" data-testid="homes-search-empty">
                No listings match these filters. Try widening your search.
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2" data-testid="homes-search-results">
                {listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
              </div>
            )}
          </div>

          {/* Map */}
          <div className={`lg:sticky lg:top-[68px] lg:self-start ${view === "list" ? "hidden lg:block" : ""}`}>
            <ListingMap points={points} height={view === "map" ? 560 : 640} />
          </div>
        </div>
      </div>
    </div>
  );
}
